/** Saved appearance is independent of the palette. A missing mode follows System, except the old dark palettes. */
export type AppearanceMode = 'light' | 'dark' | 'system';
export type AppearanceTone = 'light' | 'dark';

export const APPEARANCE_MODES = [
  { id: 'light', label: 'Light', description: 'Always light' },
  { id: 'dark', label: 'Dark', description: 'Always dark' },
  { id: 'system', label: 'System', description: 'Match this device' },
] as const;

/** The retired Dark palette (2026-10-27). A saved `dark` shows as Green in Dark mode; index.html matches. */
export const RETIRED_DARK_PALETTE = 'dark';

export function isAppearanceMode(value: unknown): value is AppearanceMode {
  return value === 'light' || value === 'dark' || value === 'system';
}

export function appearanceModeFor(value: unknown, legacyColor?: string | null): AppearanceMode {
  if (isAppearanceMode(value)) return value;
  return legacyColor === RETIRED_DARK_PALETTE || legacyColor === 'neural-ink' ? 'dark' : 'system';
}

/** The palette a saved color shows: the retired Dark palette reads as Green. */
export function paletteFor(colorTheme: string | null | undefined): string | null {
  return colorTheme === RETIRED_DARK_PALETTE ? 'green' : colorTheme ?? null;
}

export function appearanceToneFor(mode: AppearanceMode, deviceDark: boolean): AppearanceTone {
  return mode === 'system' ? (deviceDark ? 'dark' : 'light') : mode;
}

/** Reuse the established contrast/layout rules; data-color-scheme carries the person's palette. */
export function appearanceBaseTheme(colorTheme: string, tone: AppearanceTone): string {
  if (tone === 'dark') return colorTheme === 'neural-ink' ? 'neural-ink' : 'dark';
  return colorTheme === 'neural-ink' ? 'green' : colorTheme;
}
